import { Dialog, DialogContent } from '@mui/material';
import { ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAttachmentDetails } from 'api';
import { ReactComponent as DeleteIcon } from 'assets/icons/newTrash.svg';
import { Spinner, alert } from 'components';

import { ImageWrapper, PreviewImage, RemoveCircle, SpinnerW, StyledImage, Thumb } from './styled';

interface ImageAttachmentProps {
  postId?: string;
  attachmentId: string;
  fileName: string;
  fileUrl: string;
  largeFileUrl?: string;
  handleDelete?: () => void;
  blockDelete?: boolean;
}

export function ImageAttachment({
  postId,
  attachmentId,
  fileName,
  fileUrl,
  largeFileUrl,
  handleDelete,
  blockDelete,
}: ImageAttachmentProps): ReactElement {
  const { isLoading, data } = useAttachmentDetails({ postId, attachmentId, fileName, fileUrl });
  const { t } = useTranslation();
  const [previewOpen, setPreviewOpen] = useState(false);

  const resolvedLargeFileUrl = largeFileUrl ?? fileUrl.replace('/small.', '/large.');
  const { data: largeData } = useAttachmentDetails({
    postId,
    attachmentId,
    fileName: `${fileName}_large`,
    fileUrl: resolvedLargeFileUrl,
  });

  async function handleAttachmentRemove() {
    if (blockDelete) {
      alert.error(t('toasts.document.blockDeleteAttachment'));
      return;
    }

    handleDelete?.();
  }

  if (isLoading) {
    return (
      <SpinnerW>
        <Spinner size="1.5rem" wrapperSx={{ height: '60px' }} />
      </SpinnerW>
    );
  }

  function handleOnLoad() {
    window.URL.revokeObjectURL(data);
  }

  return (
    <>
      <Thumb onClick={() => setPreviewOpen(true)}>
        <ImageWrapper>
          <StyledImage alt={fileName} src={data} onLoad={handleOnLoad} />
        </ImageWrapper>

        <RemoveCircle
          className="remove-btn"
          onClick={(e) => {
            e.stopPropagation();
            handleAttachmentRemove();
          }}
        >
          <DeleteIcon />
        </RemoveCircle>
      </Thumb>
      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)}>
        <DialogContent sx={{ p: 1 }}>
          <PreviewImage alt={fileName} src={largeData || data} />
        </DialogContent>
      </Dialog>
    </>
  );
}
